import { useEffect, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Divider, Stack, Typography } from '@mui/material';
import ErrorMessage from '../errorMessage';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { GridColDef } from '@mui/x-data-grid';
import DataGridTable from '../datagridTable';

type Props = {
  csvContent: string;
  maxHeight?: number;
  showLabel?: boolean;
  label?: string;
  error?: string;
  separator?: string;
};

const CsvViewer = ({ csvContent, showLabel = true, label, error, maxHeight = 0, separator }: Props) => {
  const [headerRef, headerSize] = useElementSize();

  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    if (!csvContent) {
      setColumns([]);
      setRows([]);
      return;
    }

    // Auto-detect separator if not explicitly given
    let sep = separator;
    if (!sep) {
      const firstLine = csvContent.split('\n')[0] || '';
      if (firstLine.includes('\t')) sep = '\t';
      else if (firstLine.includes(';') && !firstLine.includes(',')) sep = ';';
      else sep = ',';
    }

    const rawRows = csvContent.split('\n').map((r) => r.trim()).filter(Boolean);
    const cols: GridColDef[] = [];
    if (rawRows.length > 0) {
      const headers = rawRows[0].split(sep);
      for (const header of headers) {
        const cleanHeader = header.trim().replace(/^["']|["']$/g, '');
        cols.push({
          field: cleanHeader || `col_${cols.length}`,
          headerName: cleanHeader || `Column ${cols.length + 1}`,
          flex: 1,
          minWidth: 100,
        });
      }
    }

    const tableRows = [];
    for (let i = 1; i < rawRows.length; i++) {
      const dataValues = rawRows[i].split(sep);
      const row: any = { id: i };
      let colCnt = 0;
      for (const header of cols) {
        const val = dataValues[colCnt] ?? '';
        row[header.field] = val.trim().replace(/^["']|["']$/g, '');
        colCnt++;
      }
      tableRows.push(row);
    }
    setColumns(cols);
    setRows(tableRows);
  }, [csvContent, separator]);

  const computedHeight =
    maxHeight > 0
      ? `${Math.max(180, maxHeight - (headerSize.height > 0 ? headerSize.height + 5 : 0))}px`
      : '320px';

  return (
    <Card variant="outlined" sx={{ border: label || error ? 1 : 0, borderColor: 'divider', width: '100%', maxWidth: '100%' }}>
      {showLabel && (label || error) && (
        <CardHeader
          title={
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems={'center'}>
              <Typography sx={{ textOverflow: 'ellipsis', fontWeight: 600, fontSize: '0.8rem' }}>{label}</Typography>
            </Stack>
          }
          subheader={error && <ErrorMessage error={error} />}
          sx={{ p: 1 }}
          ref={headerRef}
          disableTypography
        />
      )}
      {showLabel && (label || error) && <Divider />}
      <CardContent sx={{ height: computedHeight, p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ height: '100%', width: '100%', overflow: 'auto' }}>
          <DataGridTable rows={rows} columns={columns} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default CsvViewer;
